import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageTitle, statusColor } from "@/components/DashUI";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUSES = ["APPLICANT", "REVIEW", "COMPLIANCE_REVIEW", "AGREEMENT_PENDING", "APPROVED", "ACTIVE", "INACTIVE"];

export default function ProvidersAdmin() {
  const [providers, setProviders] = useState([]);
  const [sel, setSel] = useState(null);
  const [arrangement, setArrangement] = useState("");
  const load = () => api.get("/admin/providers").then((r) => setProviders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (id, body) => { await api.patch(`/admin/providers/${id}`, body); toast.success("Updated"); load(); };

  return (
    <div>
      <PageTitle title="Providers" subtitle="Approve providers and manage agreements — compensation is never auto-approved" />
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="providers-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>{["Provider", "Location", "Services", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {providers.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`provider-${p.id}`}>
                  <td className="px-4 py-3 font-medium text-navy">{p.provider_name}</td>
                  <td className="px-4 py-3 text-slate-600">{p.city}, {p.state}</td>
                  <td className="px-4 py-3 text-slate-600">{(p.service_categories || []).slice(0, 2).join(", ")}</td>
                  <td className="px-4 py-3"><Badge className={`${statusColor(p.status)} hover:${statusColor(p.status)}`}>{p.status}</Badge></td>
                  <td className="px-4 py-3"><Button size="sm" variant="outline" onClick={() => { setSel(p); setArrangement(p.commission_arrangement || ""); }} data-testid={`provider-manage-${p.id}`}>Manage</Button></td>
                </tr>
              ))}
              {providers.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No providers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{sel?.provider_name}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-4 text-sm">
              <p className="text-slate-600">{sel.city}, {sel.state} · {sel.provider_type}</p>
              <p className="text-slate-600">{(sel.service_categories || []).join(", ")}</p>
              {sel.email && <p className="text-slate-500">{sel.email} · {sel.phone}</p>}
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</label>
                <Select defaultValue={sel.status} onValueChange={(v) => update(sel.id, { status: v })}>
                  <SelectTrigger className="mt-1" data-testid="provider-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Commission arrangement (requires written agreement)</label>
                <Input data-testid="provider-arrangement" className="mt-1" value={arrangement} onChange={(e) => setArrangement(e.target.value)} placeholder="e.g. 100% first month, agent 50%" />
              </div>
              <div className="flex gap-2">
                <Button className="flex-1 bg-emeraldbrand hover:bg-emeraldbrand/90" data-testid="provider-approve-btn" onClick={() => update(sel.id, { status: "APPROVED" })}>Approve provider</Button>
                <Button variant="outline" className="flex-1" data-testid="provider-save-arrangement" onClick={() => update(sel.id, { commission_arrangement: arrangement, agreement_status: "On File" })}>Save arrangement</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
